import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Hero } from '../components/Hero';
import { PlatformExplore } from '../components/PlatformExplore';
import { Problem } from '../components/Problem';
import { Horizon2028 } from '../components/Horizon2028';
import { Approach } from '../components/Approach';
import { Proven } from '../components/Proven';
import { Difference } from '../components/Difference';
import { PlatformExperiences } from '../components/PlatformExperiences';
import { Method } from '../components/Method';
import { Ecosystems } from '../components/Ecosystems';
import { useLanguage } from '../components/LanguageContext';
import { Footer } from '../components/Footer';
import { trackEvent as track } from '../utils/analytics';
import { ContactLinks } from '../components/contact/ContactLinks';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function Home() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Home'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8510" data-mp="2mwgvj5nja4qo">
      <Header data-id="element-8511" data-mp="2hxnmbpfa4zwb" />

      <main data-id="element-8512" data-mp="2hy6zytxwd24j">
        {/* SECTION 1: HERO */}
        <Hero data-id="element-8513" data-mp="2i00c2ora4ejx" />

        {/* Platform exploration index */}
        <PlatformExplore data-id="element-8514" data-mp="2d65srmfl61ld" />

        {/* SECTION 2: THE PROBLEM — capacity reframe */}
        <Problem data-id="element-8515" data-mp="287cfnv47oclq" />
        <Horizon2028 data-id="element-8516" data-mp="288vv5ji1mjmg" />

        {/* SECTION 3: OUR APPROACH — the connected flow + ATNA layer */}
        <Approach data-id="element-8517" data-mp="23ac81frpr1si" />

        {/* SECTION 4: PROVEN IN PRODUCTION */}
        <Proven data-id="element-8518" data-mp="1z7xp6vo1o218" />

        {/* SECTION 5: THE DIFFERENCE — verified identity + verification */}
        <Difference data-id="element-8519" data-mp="1u8l0s6hiymwj" />

        {/* SECTION 6: THE PLATFORM — three experiences on one foundation */}
        <PlatformExperiences data-id="element-8520" data-mp="1kk7g6tsfdaf8" />

        {/* SECTION 7: OUR APPROACH — how we implement, step by step */}
        <Method data-id="element-8521" data-mp="1omu2kf6bu351" />

        {/* SECTION 8: OUR ECOSYSTEMS — one foundation, many sectors */}
        <Ecosystems data-id="element-8522" data-mp="1amt2h1xvyzqm" />

        {/* SECTION 9: FINAL CTA */}
        <section className="py-28 lg:py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8523" data-mp="1aonm8cxf8lt4">
          <div className="max-w-3xl mx-auto text-center" data-id="element-8524" data-mp="1flw6qk61l1xj">
            {/* Brand signature — same assets as the site header */}
            <motion.div {...reveal} className="flex items-center justify-center mb-8" data-id="element-8525" data-mp="28abjf01pb85p">
              <img src="https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png" alt="BCITI" className="h-12 w-auto object-contain" data-id="element-8526" data-mp="1tm6ydpf6ekjr" />
              <span className="mx-3.5 text-[#121212]/25 font-sans text-xl font-light select-none" aria-hidden="true" data-id="element-8527" data-mp="2m9av77447skg">
                |
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png" alt="ATNA" className="h-[2.55rem] w-auto object-contain" data-id="element-8528" data-mp="fkiwwzzkqqp4" />
            </motion.div>
            <motion.h2 {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-8529" data-mp="kcir0bsyqpds">
              {isFR ? 'Prêt à augmenter votre capacité ?' : 'Ready to increase your capacity?'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-10 flex flex-wrap items-center justify-center gap-y-1" data-id="element-8530" data-mp="ojjd9tp3ycl">
              <span data-id="element-8531" data-mp="1c62qhdaeclgf">{isFR ? 'Découvrez' : 'See'}</span>
              {/* Same logo assets as the site header */}
              <img src="https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png" alt="BCITI" className="h-11 w-auto object-contain ml-3" data-id="element-8532" data-mp="2ccztciyv81al" />
              <span className="mx-2 text-[#121212]/25 font-sans text-lg font-light select-none" aria-hidden="true" data-id="element-8533" data-mp="2mbfiigec10hg">
                |
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png" alt="ATNA" className="h-[2.4rem] w-auto object-contain" data-id="element-8534" data-mp="2vb28m61m2jv7" />
              <span className="ml-2.5" data-id="element-8535" data-mp="3513yn3diyccr">
                {isFR ? 'à l’œuvre dans votre propre environnement.' : 'in action in your own environment.'}
              </span>
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-8536" data-mp="3e08svxi1zjj4">
              <Link to="/request-demo" onClick={() => track('demo_cta_click', {
              location: 'home_final_cta'
            })} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#4F46E5]/90 transition-colors w-full sm:w-auto" data-id="element-8537" data-mp="tfcvbins7j4g">
                {isFR ? 'Demander une démo' : 'Request a demo'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-8538" data-mp="1cb30tfuxaxn3" />
              </Link>
              <ContactLinks location="home_final_cta" variant="inline" className="justify-center sm:ml-4" data-id="element-8539" data-mp="17d9xcul08hqi" />
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-8540" data-mp="202gsdjl92p70" />
    </div>;
}